import './BetbookAnalysisOverview.less';
import './BetbookAnalysisOverview.dark.less';

import BetbookPostInfoBar from '../betbook-post-info-bars/BetbookPostInfoBar';
import BetbookRewardProgressBar from '../betbook-reward-progress-bar/BetbookRewardProgressBar';
import BetbookScoreboard from '../betbook-scoreboard/BetbookScoreboard';
import BetbookUserHeader from '../betbook-analysis-user-header/BetbookUserHeader';
import Markdown from '../markdown/Markdown';
import NuiDate from '../nui-date/NuiDate';
import React from 'react';
import clsx from 'clsx';
import i18n from '../../common/i18n';
import moment from 'moment';
import { BetbookAnalysisOverviewProps } from './types';
import { BetbookContextProvider } from '../../common/BetbookContext';
import { TURBO_LIVE_ROOT_PATH } from '../../common/routing';
import { getCalendarFormattedDate } from '../betbook/utils';
import { useActiveRoute } from '../../common/routing/NikeRouteContext';

export default function BetbookAnalysisOverview({
    betComponent,
    betbookRewardProgressBarChildren,
    className,
    isPhone = false,
    openProfileDialog,
    ownPost,
    post
}: BetbookAnalysisOverviewProps) {
    const { id: postId, text, creationDate, modificationDate, commentCount = 0 } = post || {};
    const { inspirationCount = 0, author, pick, viewCount, reward, ticket: postTicket, title } = post || {};
    const { status = 'HIDDEN', value, maxValue, bonus, bonusPercent } = reward || {};
    const { evaluatedBets, successfulBets } = author || {};
    const { betId, betSelectionCode, expiration, matchHeaders, sportId = 0, tournament } = pick || {};
    const route = useActiveRoute();
    const successRate = (successfulBets / evaluatedBets) * 100;
    const creationDateFormatted = getCalendarFormattedDate(moment(creationDate));
    const isScoreboardVisible = !(route.path.startsWith(TURBO_LIVE_ROOT_PATH) && !isPhone);

    return (
        <BetbookContextProvider
            betbookPostId={postId}
            betbookPostType="ANALYSIS"
            betbookPostAuthor={author?.contractId}
            betbookBetId={betId}
            betbookBetSelectionCode={betSelectionCode}
        >
            <div
                className={clsx('overflow-hidden', className, { 'analysis-overview-wrapper': !className })}
                data-atid="betbook-analysis-item"
                data-betbook-nickname={author?.nickname}
                data-betbook-type="ANALYSIS"
                data-betbook-id={postId}
                data-betbook-title={title}
            >
                {isScoreboardVisible && (
                    <div className="analysis-overview-scoreboard-wrapper">
                        <BetbookScoreboard
                            className="analysis-overview-scoreboard"
                            matchStartDate={expiration}
                            participants={matchHeaders}
                            sportId={sportId}
                            tournamentName={tournament}
                        />
                        {reward && !isPhone && (
                            <BetbookRewardProgressBar
                                className="mt-10"
                                status={reward ? status : 'HIDDEN'}
                                value={value}
                                maxValue={maxValue}
                                bonus={bonus}
                                bonusPercent={bonusPercent}
                            >
                                {betbookRewardProgressBarChildren}
                            </BetbookRewardProgressBar>
                        )}
                    </div>
                )}
                <div className="analysis-overview-center">
                    <BetbookUserHeader
                        author={author}
                        onProfileClick={openProfileDialog}
                        ownPost={ownPost}
                        successRate={successRate}
                    />
                    <div className="analysis-overview-desktop-text-bet-wrapper">
                        <div
                            className="analysis-overview-text-wrapper preview line-clamp-3"
                            data-atid="betbook-analysis-text"
                        >
                            <NuiDate>
                                {creationDateFormatted}
                                {modificationDate && ` • ${i18n('betbook.post.edited')}`}
                            </NuiDate>
                            {text && <Markdown forceInline>{text}</Markdown>}
                        </div>
                        <div className="analysis-overview-bet-wrapper">
                            <div className="analysis-overview-bet">{betComponent}</div>
                        </div>
                    </div>
                    <BetbookPostInfoBar
                        inspirations={inspirationCount}
                        deposit={postTicket?.stake}
                        seen={viewCount}
                        comments={!isPhone && commentCount}
                    />
                    {reward && isPhone && (
                        <BetbookRewardProgressBar
                            className="mt-10"
                            status={status}
                            value={value}
                            maxValue={maxValue}
                            bonus={bonus}
                            bonusPercent={bonusPercent}
                        >
                            {betbookRewardProgressBarChildren}
                        </BetbookRewardProgressBar>
                    )}
                </div>
            </div>
        </BetbookContextProvider>
    );
}
